import { useEffect, useRef } from 'react'
import type { CameraSetting } from 'simli-client'

import type { CallAction, CallActionKind } from '@/components/coach-call'
import type { VoiceCallPhase } from '@/hooks/use-voice-call'
import { onCelebrate } from '@/lib/celebrate'
import { useCoachCall } from '@/lib/coach-call-store'

// Camera moves on the live coach (Trinity-2) so the face feels present: a lean-in
// while you talk, a glance away while thinking, a nod or a happy turn when the coach
// does something for you, a look when you open something, and a short drag that
// springs back. All of it is the camera; the face itself only lip-syncs.

type Pose = Pick<CameraSetting, 'orbit_x_off' | 'orbit_y_off' | 'rot_z_off' | 'zoom'>

const PHASE: Record<VoiceCallPhase, Pose> = {
  listening: { rot_z_off: -2.5, zoom: 1.04 },
  thinking: { orbit_y_off: 9, orbit_x_off: 3 },
  speaking: {},
}
const NOD: Pose[] = [{ orbit_x_off: 6 }, { orbit_x_off: -2 }, { orbit_x_off: 3 }]
const CHEER: Pose[] = [
  { orbit_y_off: -10, zoom: 1.08 },
  { orbit_y_off: 8, zoom: 1.08 },
]
const GLANCE: Pose = { orbit_y_off: -14, orbit_x_off: 4 }
const NODS: CallActionKind[] = [
  'commitment',
  'today',
  'task',
  'habit',
  'goal',
  'goalTask',
  'deadline',
]
const CHEERS: CallActionKind[] = ['done', 'logged']
const STEP_S = 0.22
const DRAG_YAW = 25
const DRAG_PITCH = 12

type Camera = { setFaceCamera: (c: CameraSetting | null, lerpSeconds?: number) => boolean }

export function useCoachCamera({
  call,
  phase,
  actions,
  live,
  still,
}: {
  call: Camera
  phase: VoiceCallPhase
  actions: CallAction[]
  // The live Trinity-2 face is on screen.
  live: boolean
  // Reduced motion: keep only what the user does (drag, closer).
  still: boolean
}) {
  const zoomBase = useRef(1)
  const drag = useRef({ x: 0, y: 0 })
  const moment = useRef<Pose | null>(null)
  const phaseRef = useRef(phase)
  phaseRef.current = phase
  const timers = useRef<ReturnType<typeof setTimeout>[]>([])
  const seen = useRef(new Set<string>())
  const callRef = useRef(call)
  callRef.current = call

  const apply = (lerp: number) => {
    if (!live) return
    const auto = still ? {} : { ...PHASE[phaseRef.current], ...moment.current }
    callRef.current.setFaceCamera(
      {
        rot_z_off: auto.rot_z_off ?? 0,
        orbit_x_off: (auto.orbit_x_off ?? 0) + drag.current.x,
        orbit_y_off: (auto.orbit_y_off ?? 0) + drag.current.y,
        zoom: zoomBase.current * (auto.zoom ?? 1),
      },
      lerp,
    )
  }

  const play = (steps: Pose[], holdMs = STEP_S * 1000) => {
    if (still || !live) return
    timers.current.forEach(clearTimeout)
    timers.current = []
    steps.forEach((pose, i) =>
      timers.current.push(
        setTimeout(() => {
          moment.current = pose
          apply(STEP_S)
        }, i * holdMs),
      ),
    )
    timers.current.push(
      setTimeout(() => {
        moment.current = null
        apply(0.6)
      }, steps.length * holdMs),
    )
  }

  useEffect(() => {
    if (!live) {
      zoomBase.current = 1
      drag.current = { x: 0, y: 0 }
      moment.current = null
    }
    return () => {
      timers.current.forEach(clearTimeout)
      // Leaving the full call view (minimised or ended): back to the avatar's own framing.
      if (live) callRef.current.setFaceCamera(null, 0.4)
    }
  }, [live])

  useEffect(() => {
    if (!moment.current) apply(0.8)
  }, [phase, live, still]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fresh = actions.filter((a) => !seen.current.has(a.id))
    fresh.forEach((a) => seen.current.add(a.id))
    const last = fresh.at(-1)
    if (!last || last.undone) return
    if (CHEERS.includes(last.kind)) play(CHEER, 300)
    else if (NODS.includes(last.kind)) play(NOD)
  }, [actions]) // eslint-disable-line react-hooks/exhaustive-deps

  // Your own ticks (any checkbox in the app) get the same happy turn as the coach's.
  useEffect(() => onCelebrate(() => play(CHEER, 300)), [live, still]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!live) return
    let key = useCoachCall.getState().viewing.at(-1)?.key
    return useCoachCall.subscribe((s) => {
      const next = s.shareViewing ? s.viewing.at(-1)?.key : undefined
      if (!next || next === key) return
      key = next
      play([GLANCE], 900)
    })
  }, [live]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    reset: () => {
      zoomBase.current = 1
      apply(0.55)
    },
    closer: () => {
      zoomBase.current = 1.22
      apply(0.55)
    },
    // Degrees dragged since the press; capped, and released back to centre by `release`.
    drag: (dx: number, dy: number) => {
      drag.current = {
        y: Math.max(-DRAG_YAW, Math.min(DRAG_YAW, dx * 0.25)),
        x: Math.max(-DRAG_PITCH, Math.min(DRAG_PITCH, -dy * 0.15)),
      }
      apply(0)
    },
    release: () => {
      drag.current = { x: 0, y: 0 }
      apply(0.6)
    },
  }
}
